import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  Bot,
  Brain,
  Check,
  ChevronDown,
  Search,
  Upload,
  X,
  Zap,
} from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from '../../Context/authContext';
import agentService from '../../services/agentService';
import toolsService from '../../services/toolsService';
import skillsService from '../../services/skillsService';
import AgentSuccessModal from '../AgentSuccessModal';
import Loaders from '../Loaders';
import Toast from '../Toast';
import { formatSkillName, getSkillId, normalizeSkillsList } from '../SkillsSection/skillsUtils';
import { BuilderPage } from './AgentBuilder.styles';

const DOMAIN_ID = 'f0cbb80e-e41f-46b7-921c-9fa5b90d6a04';
const accentColors = ['#03a08d', '#8B5CF6', '#10B981', '#F59E0B', '#EC4899', '#3B82F6', '#EF4444', '#14B8A6'];

const instructionTemplates = [
  {
    id: 'assistant',
    label: 'General Assistant',
    text: 'You are a helpful, concise assistant. Answer questions accurately and ask clarifying questions when needed.',
  },
  {
    id: 'researcher',
    label: 'Research Analyst',
    text: 'You are a rigorous research analyst. Investigate topics thoroughly, cite sources, and present structured findings with key insights and recommendations.',
  },
  {
    id: 'writer',
    label: 'Content Writer',
    text: 'You are an expert content writer. Produce clear, engaging copy tailored to the audience. Adapt tone and style on request.',
  },
  {
    id: 'coder',
    label: 'Code Engineer',
    text: 'You are a senior software engineer. Write clean, well-tested code. Explain decisions, flag edge cases, and suggest improvements.',
  },
  {
    id: 'support',
    label: 'Support Agent',
    text: "You are a patient customer support agent. Resolve issues empathetically, escalate when appropriate, and always confirm the user's problem is solved.",
  },
  {
    id: 'analyst',
    label: 'Data Analyst',
    text: 'You are a data analyst. Interpret data, surface actionable insights, and present results with clear visual descriptions and caveats.',
  },
];

const agentTypes = [
  { id: 'generative', label: 'Generative Agent' },
  { id: 'copilot', label: 'Copilot Agent' },
  { id: 'persona', label: 'Persona Agent' },
  { id: 'action', label: 'Action Agent' },
];

function normalizeIdList(value) {
  if (value == null || value === '') return [];
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith('[')) {
      try {
        return normalizeIdList(JSON.parse(trimmed));
      } catch {
        /* fall through */
      }
    }
    return trimmed
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
  }
  if (Array.isArray(value)) {
    return value.flatMap(item => {
      if (item == null || item === '') return [];
      if (typeof item === 'string' || typeof item === 'number') return [String(item)];
      if (typeof item === 'object') {
        const id = item.skill_id || item.tool_id || item.id || item.value;
        return id != null && id !== '' ? [String(id)] : [];
      }
      return [];
    });
  }
  return [];
}

function parseModelProviders(modelsData) {
  const raw = modelsData?.models;
  if (!Array.isArray(raw)) return [];
  return raw.map(providerObj => {
    const providerName = Object.keys(providerObj)[0];
    const models = providerObj[providerName] || [];
    return {
      id: providerName,
      label: providerName,
      models: models.map(m => ({ id: m.id, label: m.name || m.id })),
    };
  });
}

function findProviderForModel(providers, modelId) {
  return providers.find(p => p.models.some(m => m.id === modelId))?.id || '';
}

function resolveIconSrc(icon) {
  if (!icon || typeof icon !== 'string') return null;
  if (icon.startsWith('http://') || icon.startsWith('https://')) {
    return icon.split('?')[0].toLowerCase().endsWith('.svg') ? null : icon;
  }
  if (icon.startsWith('data:')) return icon;
  return null;
}

function SimpleSelect({ label, value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const current = options.find(o => o.id === value);
  const filtered = query
    ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  return (
    <div className="field select">
      <label>{label}</label>
      <button type="button" className="select-trigger" onClick={() => setOpen(v => !v)}>
        <span className={current ? '' : 'placeholder'}>{current ? current.label : `Select ${label.toLowerCase()}`}</span>
        <ChevronDown size={16} color="#707070" />
      </button>
      {open && (
        <>
          <div className="backdrop" onClick={() => setOpen(false)} />
          <div className="menu">
            <div style={{ position: 'relative', padding: 8, borderBottom: '1px solid #262626' }}>
              <Search
                size={14}
                color="#606060"
                style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)' }}
              />
              <input
                autoFocus
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder={`Search ${label.toLowerCase()}...`}
                style={{ paddingLeft: 32 }}
              />
            </div>
            {filtered.length === 0 && <p className="empty">No results found.</p>}
            {filtered.map(o => (
              <button
                key={o.id}
                type="button"
                className="option"
                onClick={() => {
                  onChange(o.id);
                  setOpen(false);
                  setQuery('');
                }}>
                <span>{o.label}</span>
                {o.id === value && <Check size={14} color="var(--primary)" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function MultiSelect({ label, value, onToggle, options }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="field select">
      <label>{label}</label>
      <button type="button" className="select-trigger" onClick={() => setOpen(v => !v)}>
        <span className={value.length ? '' : 'placeholder'}>
          {value.length ? `${value.length} selected` : 'Select to add'}
        </span>
        <ChevronDown size={16} color="#707070" />
      </button>
      {open && (
        <>
          <div className="backdrop" onClick={() => setOpen(false)} />
          <div className="menu">
            {options.length === 0 && <p className="empty">None available</p>}
            {options.map(opt => (
              <button key={opt.id} type="button" className="option" onClick={() => onToggle(opt.id)}>
                <span>{opt.label}</span>
                {value.includes(opt.id) && <Check size={14} color="#10B981" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function AgentBuilder({ agentId, isDuplicate = false }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  const { user } = useContextHook(AuthContext, v => ({ user: v.user }));
  const [successModal, setSuccessModal] = useState(false);
  const isEdit = !!agentId && !isDuplicate;

  const agentsQuery = useQuery({
    queryKey: ['get-all-agents'],
    queryFn: () => agentService.getAllAgent(),
    staleTime: Infinity,
  });

  const modelsQuery = useQuery({
    queryKey: ['models'],
    queryFn: () => agentService.getAllModels(),
    staleTime: Infinity,
  });

  const userToolsQuery = useQuery({
    queryKey: ['tools', 'user_created'],
    queryFn: () => toolsService.getAllTools('user_created'),
    staleTime: Infinity,
  });

  const adminToolsQuery = useQuery({
    queryKey: ['tools', 'admin_created'],
    queryFn: () => toolsService.getAllTools('admin_created'),
    staleTime: Infinity,
  });

  const skillsQuery = useQuery({
    queryKey: ['skills'],
    queryFn: () => skillsService.getAllSkills(),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const detailQuery = useQuery({
    queryKey: ['get-single-agent', agentId],
    queryFn: () => agentService.getAgentDetail(agentId),
    enabled: !!agentId,
  });

  const agentDetail = detailQuery.data?.detail;
  const modelProviders = useMemo(() => parseModelProviders(modelsQuery.data), [modelsQuery.data]);
  const toolOptions = useMemo(() => {
    const userTools = userToolsQuery.data?.tools || [];
    const adminTools = adminToolsQuery.data?.tools || [];
    return [...userTools, ...adminTools].map(t => ({
      id: String(t.tool_id),
      label: t.tool_name,
    }));
  }, [userToolsQuery.data, adminToolsQuery.data]);

  const skillOptions = useMemo(
    () =>
      normalizeSkillsList(skillsQuery.data)
        .map(s => {
          const id = getSkillId(s);
          if (id == null || id === '') return null;
          return {
            id: String(id),
            label: formatSkillName(s?.name || s?.skill_name),
          };
        })
        .filter(Boolean),
    [skillsQuery.data],
  );

  const [name, setName] = useState('');
  const [iconColor, setIconColor] = useState(accentColors[0]);
  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);
  const [agentType, setAgentType] = useState('generative');
  const [template, setTemplate] = useState(null);
  const [instructions, setInstructions] = useState('');
  const [description, setDescription] = useState('');
  const [provider, setProvider] = useState('');
  const [model, setModel] = useState('');
  const [tools, setTools] = useState([]);
  const [skills, setSkills] = useState([]);
  const [memory, setMemory] = useState(false);
  const [triggers, setTriggers] = useState(true);
  const [prefilled, setPrefilled] = useState(false);

  useEffect(() => {
    setPrefilled(false);
  }, [agentId]);

  useEffect(() => {
    if (!provider && modelProviders.length > 0 && !agentId) {
      setProvider(modelProviders[0].id);
      if (modelProviders[0].models?.[0]) setModel(modelProviders[0].models[0].id);
    }
  }, [modelProviders, provider, agentId]);

  useEffect(() => {
    if (!agentId || prefilled || !agentDetail || modelProviders.length === 0) return;

    setName(isDuplicate ? `${agentDetail.agent_name || ''} Copy` : agentDetail.agent_name || '');
    setDescription(agentDetail.description || '');
    setInstructions(agentDetail.instructions || '');
    setMemory(agentDetail.enable_user_memories ?? false);
    if (agentDetail.behavior_type) setAgentType(agentDetail.behavior_type);
    if (agentDetail.model_id) {
      setModel(agentDetail.model_id);
      setProvider(findProviderForModel(modelProviders, agentDetail.model_id));
    }
    setTools(normalizeIdList(agentDetail.tools));
    setSkills(normalizeIdList(agentDetail.skills));
    if (!isDuplicate) {
      const src = resolveIconSrc(agentDetail.icon);
      if (src) setIconPreview(src);
    }
    setPrefilled(true);
  }, [agentId, prefilled, agentDetail, modelProviders, isDuplicate]);

  const createAgent = useMutation({
    mutationFn: payload => agentService.createAgent(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['get-all-agents'] });
      setSuccessModal(true);
    },
    onError: err => {
      Toast({ type: 'error', message: err?.detail || err?.message || 'Could not create agent' });
    },
  });

  const updateAgent = useMutation({
    mutationFn: payload => agentService.EditAgent(payload),
    onSuccess: response => {
      queryClient.invalidateQueries({ queryKey: ['get-all-agents'] });
      Toast({ type: 'success', message: response?.detail || 'Agent updated' });
      navigate('/agents');
    },
    onError: err => {
      Toast({ type: 'error', message: err?.detail || err?.message || 'Could not update agent' });
    },
  });

  const svgToPngFile = (svgMarkup, fileName, size = 128) =>
    new Promise((resolve, reject) => {
      const blobUrl = URL.createObjectURL(new Blob([svgMarkup], { type: 'image/svg+xml' }));
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        canvas.getContext('2d').drawImage(img, 0, 0, size, size);
        URL.revokeObjectURL(blobUrl);
        canvas.toBlob(blob => {
          if (blob) resolve(new File([blob], fileName, { type: 'image/png' }));
          else reject(new Error('Could not convert icon to PNG'));
        }, 'image/png');
      };
      img.onerror = () => {
        URL.revokeObjectURL(blobUrl);
        reject(new Error('Could not load SVG icon'));
      };
      img.src = blobUrl;
    });

  const createRobotIconFile = color => {
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64">
        <rect width="64" height="64" rx="14" fill="${color}" fill-opacity="0.12"/>
        <path d="M32 13v6M28 13h8" stroke="${color}" stroke-width="3" stroke-linecap="round"/>
        <rect x="16" y="19" width="32" height="27" rx="8" fill="none" stroke="${color}" stroke-width="3"/>
        <circle cx="26" cy="31" r="2.5" fill="${color}"/>
        <circle cx="38" cy="31" r="2.5" fill="${color}"/>
        <path d="M25 39h14M12 29v8M52 29v8M25 51v-5M39 51v-5" stroke="${color}" stroke-width="3" stroke-linecap="round"/>
      </svg>`;
    return svgToPngFile(svg.trim(), 'robot-icon.png');
  };

  const handleIconUpload = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      Toast({ type: 'error', message: 'Icon must be under 5MB' });
      return;
    }
    if (file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg')) {
      try {
        const pngFile = await svgToPngFile(await file.text(), file.name.replace(/\.svg$/i, '.png'));
        setIconFile(pngFile);
        setIconPreview(URL.createObjectURL(pngFile));
      } catch {
        Toast({ type: 'error', message: 'Could not process SVG icon' });
      }
      return;
    }
    setIconFile(file);
    setIconPreview(URL.createObjectURL(file));
  };

  const toggleIn = (setList, id) => {
    const sid = String(id);
    setList(prev => (prev.includes(sid) ? prev.filter(x => x !== sid) : [...prev, sid]));
  };

  const labelForId = (options, id) => options.find(o => o.id === id)?.label || id;

  const handleSave = async () => {
    if (!agentType) {
      Toast({ type: 'error', message: 'Please select an agent type' });
      return;
    }
    if (!name.trim() || !description.trim() || !instructions.trim() || !model) return;

    if (!isEdit) {
      const agentLimit = user?.plan?.agent_creation_limit;
      const agentCount = agentsQuery.data?.agents?.length ?? 0;
      if (agentLimit != null && agentCount >= agentLimit) {
        Toast({ type: 'error', message: 'Agent limit exceeded' });
        return;
      }
    }

    const payload = new FormData();
    if (isEdit) payload.append('agent_id', agentId);

    if (iconFile instanceof File) {
      payload.append('icon', iconFile);
    } else if (!isEdit || !iconPreview) {
      try {
        payload.append('icon', await createRobotIconFile(iconColor));
      } catch {
        Toast({ type: 'error', message: 'Could not generate agent icon' });
        return;
      }
    }

    payload.append('agent_name', name.trim());
    payload.append('description', description.trim());
    payload.append('instructions', instructions.trim());
    payload.append('model_id', model);
    payload.append('domain_id', DOMAIN_ID);
    payload.append('tools', normalizeIdList(tools).join(','));
    payload.append('skills', normalizeIdList(skills).join(','));
    payload.append('enable_user_memories', memory);
    payload.append('platform_id', 'gramielabs');
    payload.append('agent_type', 'user_created');
    payload.append('behavior_type', agentType);
    if (user?.data?.ID) payload.append('user_id', user.data.ID);

    if (isEdit) updateAgent.mutate(payload);
    else createAgent.mutate(payload);
  };

  const valid = Boolean(agentType) && name.trim() && description.trim() && instructions.trim() && model;
  const activeProviderModels = modelProviders.find(p => p.id === provider)?.models || [];
  const saving = createAgent.isPending || updateAgent.isPending;

  if (agentId && detailQuery.isFetching && !prefilled) {
    return <Loaders pageLoader />;
  }

  return (
    <>
      {successModal && <AgentSuccessModal onClose={() => setSuccessModal(false)} />}

      <BuilderPage>
        <button type="button" className="back" onClick={() => navigate('/agents')}>
          <ArrowLeft size={16} /> Back to Agents
        </button>

        <div className="page-title">
          <h1>{isEdit ? 'Edit Your Agent' : 'Build Your Agent'}</h1>
          <p>Name your agent, define its core instructions, configure behavior, and attach tools & skills.</p>
        </div>

        <div className="layout">
          <div className="stack">
            <div className="card">
              <h3 className="card-title">Identity</h3>
              <div className="field">
                <label>
                  <span className="req">*</span> Agent Name
                </label>
                <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Jose" />
              </div>
              <div className="field">
                <label>Icon</label>
                {iconPreview ? (
                  <div className="icon-row">
                    <div className="preview">
                      <img src={iconPreview} alt="Agent icon" />
                    </div>
                    <button
                      type="button"
                      className="remove"
                      onClick={() => {
                        setIconFile(null);
                        setIconPreview(null);
                      }}>
                      <X size={14} style={{ marginRight: 4 }} />
                      Remove image
                    </button>
                  </div>
                ) : (
                  <div className="icon-row">
                    <div className="preview" style={{ backgroundColor: `${iconColor}1A`, color: iconColor }}>
                      <Bot size={20} />
                    </div>
                    <div className="swatches">
                      {accentColors.map(c => (
                        <button
                          key={c}
                          type="button"
                          className={iconColor === c ? 'active' : ''}
                          style={{ backgroundColor: c }}
                          onClick={() => setIconColor(c)}
                          aria-label={`Color ${c}`}
                        />
                      ))}
                    </div>
                  </div>
                )}
                <button type="button" className="upload-link" onClick={() => fileInputRef.current?.click()}>
                  <Upload size={14} />
                  {iconPreview
                    ? 'Replace image (JPG, PNG, SVG · max 5mb)'
                    : 'Upload custom icon (JPG, PNG, SVG · max 5mb)'}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.svg,image/jpeg,image/png,image/svg+xml"
                  style={{ display: 'none' }}
                  onChange={handleIconUpload}
                />
              </div>
              <div className="field">
                <label>
                  Type <span style={{ color: 'var(--primary)' }}>*</span>
                </label>
                <div className="type-grid">
                  {agentTypes.map(t => (
                    <button
                      key={t.id}
                      type="button"
                      className={agentType === t.id ? 'active' : ''}
                      onClick={() => setAgentType(t.id)}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="card">
              <h3 className="card-title">Behavior</h3>
              <div className="grid-2">
                <SimpleSelect
                  label="Provider"
                  value={provider}
                  options={modelProviders.map(p => ({ id: p.id, label: p.label }))}
                  onChange={pid => {
                    setProvider(pid);
                    const p = modelProviders.find(x => x.id === pid);
                    if (p?.models?.length) setModel(p.models[0].id);
                  }}
                />
                <SimpleSelect label="Model Version" value={model} options={activeProviderModels} onChange={setModel} />
              </div>
            </div>

            <div className="card">
              <h3 className="card-title">Tools & Skills</h3>
              <MultiSelect
                label="Tools"
                value={tools}
                onToggle={id => toggleIn(setTools, id)}
                options={toolOptions}
              />
              {tools.length > 0 && (
                <div className="chips tools">
                  {tools.map(t => (
                    <span key={t}>{labelForId(toolOptions, t)}</span>
                  ))}
                </div>
              )}
              <MultiSelect
                label="Skills"
                value={skills}
                onToggle={id => toggleIn(setSkills, id)}
                options={skillOptions}
              />
              {skills.length > 0 && (
                <div className="chips skills">
                  {skills.map(s => (
                    <span key={s}>{labelForId(skillOptions, s)}</span>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="stack">
            <div className="card">
              <div className="coming-soon">
                <div className="overlay">
                  <strong>Coming Soon</strong>
                  <span>Core Instructions is under development and will be available soon.</span>
                </div>
                <div className="dimmed">
                  <h3 className="card-title">Core Instructions</h3>
                  <p className="hint" style={{ marginBottom: 12 }}>
                    Start from a template, then refine the instructions to fit your use case.
                  </p>
                  <div className="templates">
                    {instructionTemplates.map(t => (
                      <button
                        key={t.id}
                        type="button"
                        className={template === t.id ? 'active' : ''}
                        tabIndex={-1}
                        aria-hidden="true">
                        <div className="label">
                          {t.label}
                          {template === t.id && <Check size={14} color="var(--primary)" />}
                        </div>
                        <div className="text">{t.text}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="field" style={{ marginTop: 16 }}>
                <label>
                  <span className="req">*</span> Instructions
                </label>
                <textarea
                  value={instructions}
                  onChange={e => setInstructions(e.target.value)}
                  rows={10}
                  placeholder="Define what your agent does, its role, and how it should behave..."
                />
              </div>
            </div>

            <div className="field">
              <label>
                <span className="req">*</span> Description
              </label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={3}
                placeholder="Describe what this agent does"
              />
            </div>

            <div className="toggles">
              <div className="toggle-card">
                <div className="head">
                  <label>Memory</label>
                  <button
                    type="button"
                    className={`switch ${memory ? 'on' : ''}`}
                    onClick={() => setMemory(v => !v)}
                    aria-label="Toggle memory">
                    <span />
                  </button>
                </div>
                <div className="status">
                  <div className={`icon ${memory ? 'on' : ''}`}>
                    <Brain size={16} />
                  </div>
                  <span>{memory ? 'Memory Enabled' : 'Memory Disabled'}</span>
                </div>
                <p className="desc">Remember user preferences and context across conversations.</p>
              </div>

              <div className="toggle-card">
                <div className="head">
                  <label>Triggers</label>
                  <button
                    type="button"
                    className={`switch ${triggers ? 'on' : ''}`}
                    onClick={() => setTriggers(v => !v)}
                    aria-label="Toggle triggers">
                    <span />
                  </button>
                </div>
                <div className="status">
                  <div className={`icon ${triggers ? 'on' : ''}`}>
                    <Zap size={16} />
                  </div>
                  <span>{triggers ? 'Triggers Enabled' : 'Triggers Disabled'}</span>
                </div>
                <p className="desc">Activate on events and schedules.</p>
              </div>
            </div>

            <div className="footer">
              <button type="button" className="cancel" onClick={() => navigate('/agents')}>
                Cancel
              </button>
              <button type="button" className="save" onClick={handleSave} disabled={!valid || saving}>
                {saving
                  ? isEdit
                    ? 'Updating...'
                    : 'Creating...'
                  : isEdit
                    ? 'Update Agent'
                    : 'Create Agent'}
              </button>
            </div>
          </div>
        </div>
      </BuilderPage>
    </>
  );
}
